import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { ArrowUpload24Regular } from '@fluentui/react-icons';
import { AppState } from '../../../redux/reducers';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';
import { GuideProgress } from '../../../theme/components/GuideProgress';
import { StatusPill } from '../../../theme/components/StatusPill';
import {
  importJobConsumptionsLink,
  importJobProgress,
  importJobRetryLink,
  importJobStatusLabelKey,
  importJobStatusTone,
  isImportJobActive,
  isImportJobFailed,
  isImportJobSucceeded,
} from '../../../functions/ImportJobs.Functions';
import { WidgetProps } from '../Dashboard.Types';
import { recentImportJobs } from './Widgets.Functions';

/**
 * Importaciones de consumos de proveedores del usuario (las que sigue el aviso global, spec 008):
 * en curso con su progreso, terminadas con vínculo a los consumos y fallidas con reintento.
 */
export const ImportJobsWidget = (_props: WidgetProps) => {
  const { t } = useTranslation();
  const jobsById = useSelector((state: AppState) => state.ImportJobs?.jobs);
  const jobs = React.useMemo(() => recentImportJobs(jobsById), [jobsById]);

  if (jobs.length === 0) {
    return (
      <GuideEmptyState
        icon={<ArrowUpload24Regular />}
        title={t('dashboard.widgets.importJobs.empty')}
        text={t('dashboard.widgets.importJobs.emptyText')}
        data-testid="import-jobs-empty"
      />
    );
  }

  return (
    <ul className="dbw-list" data-testid="import-jobs-list">
      {jobs.map((job) => {
        const when = job.finishedAt ?? job.startedAt ?? job.requestedAt;
        return (
          <li key={job.id} className="dbw-list__row" data-testid={`import-job-${job.id}`}>
            <div className="dbw-list__main">
              <span className="dbw-list__title">{job.fileName || job.providerFileId}</span>
              <span className="dbw-list__meta">
                {t('dashboard.widgets.importJobs.provider', { id: job.providerId })}
                {when ? ` · ${moment(when).format('DD/MM/YYYY HH:mm')}` : ''}
              </span>
              {isImportJobActive(job) && <GuideProgress size="small" value={importJobProgress(job)} label={job.stage ?? undefined} className="dbw-progress" />}
            </div>
            <div className="dbw-list__side">
              <StatusPill tone={importJobStatusTone(job)} size="small">
                {t(importJobStatusLabelKey(job))}
              </StatusPill>
              {isImportJobSucceeded(job) && (
                <Link to={importJobConsumptionsLink(job)} className="dbw-list__link">
                  {t('dashboard.widgets.importJobs.seeConsumptions')}
                </Link>
              )}
              {isImportJobFailed(job) && (
                <Link to={importJobRetryLink(job)} className="dbw-list__link">
                  {t('dashboard.widgets.importJobs.retry')}
                </Link>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default ImportJobsWidget;
